import { useRef } from 'react';
import deepEqual from 'fast-deep-equal';
import { flow, invariant, option, pipe, remote, task } from '@code-expert/prelude';
import { useAsyncError } from './useAsyncError.js';
import { useDeepCompareEffect } from './useDeepCompare.js';
import { useRaceState } from './useRaceState.js';
const getOptionDefaults = (options) => options.length === 2
    ? options
    : options.length === 0
        ? [{}, []]
        : 'props' in options[0] || 'propsO' in options[0]
            ? [options[0], []]
            : [{}, options[0]];
export const useRemote = (fn, ...options) => {
    const [state, mkSetState] = useRaceState(remote.initial);
    const error = useAsyncError('in useRemote');
    const [props, deps] = getOptionDefaults(options);
    const propsO = 'props' in props ? option.some(props.props) : 'propsO' in props ? props.propsO : undefined;
    const { current: cache } = useRef({
        fn,
        prevArgs: {},
        deps: [],
        isDeclarative: propsO != null,
    });
    cache.fn = fn;
    const refreshO = (aO) => () => {
        error.reset();
        // cache.propsO = aO;
        const setState = mkSetState();
        pipe(aO, option.fold(() => {
            if (!remote.isInitial(state))
                setState(remote.initial);
        }, (a) => {
            if (!remote.isPending(state))
                setState(remote.pending);
            void task.run(cache.fn(a)).then(flow(remote.done, setState), error.abort);
        }));
    };
    const refresh = (props) => refreshO(option.some(props));
    let result = state;
    if (cache.isDeclarative) {
        invariant(propsO != null, 'React hooks must be called in the exact same order in every component render. If you provide props declaratively to useRemote once, you must provide them declaratively everytime.');
        // This MUST exactly mimic the behaviour that emerges from the useEffect(() => refreshO) call just below
        if (!deepEqual(propsO, cache.prevArgs) || !deepEqual(deps, cache.deps)) {
            // useEffect does a deepEqual comparison of propsO and a Object.equals comparison of deps
            cache.prevArgs = propsO; // useEffect stores the previous invocations' dependencies
            error.reset(); // refreshO first resets any possible error state
            cache.deps = deps; // useEffect will store deps for comparison in the next render cycle
            if (option.isNone(propsO)) {
                // refreshO with None will set the state to initial
                if (!remote.isInitial(state)) {
                    mkSetState()(remote.initial);
                    result = remote.initial;
                }
            }
            else {
                // refreshO with Some(props) will set the state to pending
                if (!remote.isPending(state)) {
                    mkSetState()(remote.pending);
                    result = remote.pending;
                }
            }
        }
        // eslint-disable-next-line react-hooks/rules-of-hooks,react-hooks/exhaustive-deps
        useDeepCompareEffect(refreshO(propsO), [propsO, ...deps]);
    }
    error.raise();
    return [result, refresh, refreshO];
};
//# sourceMappingURL=useRemote.js.map